import { WORLD, OCEAN } from '../config';

/**
 * Vlnové pole moře — jeden zdroj pravdy pro shader i pro fyziku.
 *
 * Hladina se vykresluje na GPU Gerstnerovými vlnami, ale plavání se počítá
 * na CPU. Dokud si každá strana držela vlastní čísla, plavala postava proti
 * **rovné** hladině, zatímco vykreslená voda pod ní jezdila nahoru a dolů —
 * na hřebeni zůstala pod vodou, v prohlubni visela ve vzduchu.
 *
 * Proto tenhle modul: spektrum je definované jednou v TypeScriptu a GLSL se
 * z něj **vygeneruje**. Změna vlny se tím promítne do obojího naráz a rozejít
 * se to nemůže.
 *
 * Šum je schválně napsaný dvakrát — jednou v TS, jednou v GLSL — protože
 * generovat ho by bylo zbytečně křehké. Obě verze musí zůstat totožné; kdyby
 * se rozešly, projeví se to jako pomalé kolísání výšky plavce.
 */

export interface WaveComponent {
  /** Směr šíření (nemusí být normalizovaný). */
  dir: [number, number];
  /** Vlnová délka v metrech. */
  wavelength: number;
  /** Relativní amplituda; násobí se OCEAN.amplitude. */
  amplitude: number;
  /**
   * Fázový posun.
   *
   * Bez něj začínají všechny vlny v počátku souřadnic ve stejné fázi a
   * jejich interference vytvoří pravidelnou mřížku přes celý obzor.
   */
  phase: number;
}

/**
 * Spektrum drží zhruba konstantní strmost (k·a), jen s klesající amplitudou.
 * Dlouhá vlna dává moři velkou strukturu, krátké drobnou kresbu; kdyby byly
 * všechny podobně dlouhé, působí hladina jako jednolitá vlnitá textura.
 */
export const WAVES: readonly WaveComponent[] = [
  { dir: [1.0, 0.1], wavelength: 96.0, amplitude: 1.0, phase: 0.0 },
  { dir: [0.62, -0.78], wavelength: 57.0, amplitude: 0.55, phase: 2.31 },
  { dir: [0.18, 0.98], wavelength: 33.0, amplitude: 0.34, phase: 5.07 },
  { dir: [0.89, -0.45], wavelength: 19.0, amplitude: 0.2, phase: 1.42 },
  { dir: [-0.72, 0.69], wavelength: 11.0, amplitude: 0.12, phase: 3.88 },
  { dir: [0.47, 0.88], wavelength: 6.2, amplitude: 0.07, phase: 0.63 },
];

const GRAVITY = 9.81;
const TWO_PI = Math.PI * 2;

/** GLSL deklarace spektra. Vkládá se do shaderu vody. */
export function waveSpectrumGLSL(): string {
  const specs = WAVES.map(
    (w) => `  vec4(${w.dir[0]}, ${w.dir[1]}, ${w.wavelength}, ${w.amplitude})`,
  ).join(',\n');
  const phases = WAVES.map((w) => w.phase.toFixed(4)).join(', ');
  return `
// VYGENEROVÁNO z WAVES v src/world/waveField.ts — needitovat tady.
const int W_COUNT = ${WAVES.length};
const vec4 W_SPEC[${WAVES.length}] = vec4[${WAVES.length}](
${specs}
);
const float W_PHASE[${WAVES.length}] = float[${WAVES.length}](${phases});
`;
}

/**
 * Výška hladiny nad klidovou úrovní, na daném místě a čase.
 *
 * Musí přesně odpovídat svislé složce `wGerstner` v shaderu vody.
 *
 * Zjednodušení proti shaderu je jediné: neuplatňuje se `geoFade`, které tam
 * s rostoucí vzdáleností od kamery tlumí krátké vlny kvůli aliasingu. Hráč
 * je vždycky pár metrů od kamery, takže by stejně vyšlo skoro přesně 1.
 *
 * Vodorovný posun Gerstnerovy vlny se zanedbává — vzorkuje se rovnou na
 * souřadnicích postavy. Chyba je v řádu centimetrů a stálo by to iterativní
 * hledání, které by se za tenhle rozdíl nezaplatilo.
 */
export function waveHeightAt(x: number, z: number, time: number, shoal: number): number {
  if (shoal <= 0) return 0;

  // Vlnové skupiny — moře jde v skupinách, ne rovnoměrně.
  const group = 0.45 + 1.05 * fbm(x * 0.0055 + time * 0.013, z * 0.0055 - time * 0.009);

  let height = 0;
  for (let i = 0; i < WAVES.length; i++) {
    const wave = WAVES[i];
    const length = Math.hypot(wave.dir[0], wave.dir[1]);
    const dirX = wave.dir[0] / length;
    const dirZ = wave.dir[1] / length;

    // Krátké vlny se skupinami modulují míň — drobné vlnky jsou všude.
    const groupMix = lerp(1, group, clamp(wave.wavelength / 20, 0.25, 1));
    const amplitude = wave.amplitude * OCEAN.amplitude * shoal * groupMix;

    const k = TWO_PI / wave.wavelength;
    // Disperzní vztah hlubinné vlny: c = sqrt(g/k).
    const speed = Math.sqrt(GRAVITY / k);
    let phase = k * (dirX * x + dirZ * z) + time * speed * k + WAVES[i].phase;

    // Ohyb hřebene podél kolmice ke směru šíření.
    const bend = 0.013 + 0.011 * i;
    phase += 1.6 * Math.sin((-dirZ * x + dirX * z) * bend + time * 0.11 + wave.phase * 2);

    height += amplitude * Math.sin(phase);
  }
  return height;
}

/**
 * Útlum vln na mělčině.
 *
 * Pod hloubkou srovnatelnou s amplitudou už vlna nemá kam kmitat a láme se.
 * Stejný vztah používá shader, jinak by u břehu postava skákala po vlnách,
 * které tam nejsou vidět.
 */
export function shoalAt(groundY: number): number {
  return smoothstep(0, 9, WORLD.seaLevel - groundY);
}

/** Výška vodní hladiny ve světových souřadnicích. */
export function waterSurfaceAt(x: number, z: number, time: number, groundY: number): number {
  return WORLD.seaLevel + waveHeightAt(x, z, time, shoalAt(groundY));
}

// --- šum -------------------------------------------------------------------
// Musí odpovídat wHash/wNoise/wFbm v shaderu vody.

function hash(x: number, y: number): number {
  let px = fract(x * 0.1031);
  let py = fract(y * 0.1031);
  let pz = fract(x * 0.1031);
  const d = px * (py + 33.33) + py * (pz + 33.33) + pz * (px + 33.33);
  px = fract(px + d);
  py = fract(py + d);
  pz = fract(pz + d);
  return fract((px + py) * pz);
}

function noise(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  return lerp(
    lerp(hash(ix, iy), hash(ix + 1, iy), ux),
    lerp(hash(ix, iy + 1), hash(ix + 1, iy + 1), ux),
    uy,
  );
}

function fbm(x: number, y: number): number {
  let sum = 0;
  let amp = 0.5;
  let px = x;
  let py = y;
  for (let i = 0; i < 4; i++) {
    sum += amp * noise(px, py);
    px *= 2.09;
    py *= 2.09;
    amp *= 0.5;
  }
  return sum;
}

function fract(v: number): number {
  return v - Math.floor(v);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}
